import type {
  OffsetPageDTO,
  OffsetPageQueryDTO,
} from "@/shared/contracts/base";
import { config } from "@/shared/lib/config";
import { fetcher } from "@/shared/lib/fetcher";
import type {
  BulkDeleteDTO,
  BulkToggleStatusDTO,
  CreateStudentDTO,
  StudentDTO,
  UpdateStudentDTO,
  UploadMembershipSignedDocumentDTO,
} from "../../domain/contracts";
import type {
  ActivePackageDTO,
  ActivePrivateLessonPackageDTO,
} from "../../domain/repo/active-package.dto";
import type { AttendanceSummaryDTO } from "../../domain/repo/get-attendance-summary";
import type { StudentSummaryDTO } from "../../domain/repo/get-summary";
import type { AttendanceHistoryDTO } from "../../domain/repo/list-attendance";
import type { PrivateLessonBookingHistoryDTO } from "../../domain/repo/list-private-lesson-bookings";
import type { PrivateLessonPackageHistoryDTO } from "../../domain/repo/list-private-lesson-packages";
import type { StudentPackageDTO } from "../../domain/repo/list-packages";
import type { UpdatePackageClassSessionsDTO } from "../../domain/contracts/update-package-class-sessions";
import type { UpdatePackageSelectedClassesDTO } from "../../domain/contracts/update-package-selected-classes";

const baseUrl = `${config.apiUrl}/students`;

export const studentsApi = {
  list: (query: OffsetPageQueryDTO): Promise<OffsetPageDTO<StudentDTO>> => {
    const url = new URL(baseUrl);
    url.searchParams.set("limit", String(query.limit ?? 20));
    url.searchParams.set("offset", String(query.offset ?? 0));
    if (query.sort) url.searchParams.set("sort", JSON.stringify(query.sort));
    if (query.filters)
      url.searchParams.set("filters", JSON.stringify(query.filters));
    return fetcher.get<OffsetPageDTO<StudentDTO>>(url.toString());
  },

  get: (id: string): Promise<StudentDTO> =>
    fetcher.get<StudentDTO>(`${baseUrl}/${id}`),

  create: (input: CreateStudentDTO): Promise<StudentDTO> =>
    fetcher.post<StudentDTO>(baseUrl, input),

  update: (id: string, input: UpdateStudentDTO): Promise<StudentDTO> =>
    fetcher.put<StudentDTO>(`${baseUrl}/${id}`, input),

  setMembershipSignedDocument: (
    id: string,
    input: UploadMembershipSignedDocumentDTO,
  ): Promise<StudentDTO> =>
    fetcher.put<StudentDTO>(
      `${baseUrl}/${id}/membership-signed-document`,
      input,
    ),

  membershipSignedDocumentUrl(id: string): string {
    return `${baseUrl}/${id}/membership-signed-document`;
  },

  remove: (id: string): Promise<StudentDTO> =>
    fetcher.delete<StudentDTO>(`${baseUrl}/${id}`),

  bulkDelete: (input: BulkDeleteDTO): Promise<{ deletedCount: number }> =>
    fetcher.post<{ deletedCount: number }>(`${baseUrl}/bulk-delete`, input),

  bulkToggleStatus: (
    input: BulkToggleStatusDTO,
  ): Promise<{ updatedCount: number }> =>
    fetcher.post<{ updatedCount: number }>(
      `${baseUrl}/bulk-toggle-status`,
      input,
    ),

  getSummary: (id: string): Promise<StudentSummaryDTO> =>
    fetcher.get<StudentSummaryDTO>(`${baseUrl}/${id}/summary`),

  getActivePackage: (id: string): Promise<{ package: ActivePackageDTO }> =>
    fetcher.get<{ package: ActivePackageDTO }>(
      `${baseUrl}/${id}/active-package`,
    ),

  listActivePackages: (
    id: string,
  ): Promise<{ packages: NonNullable<ActivePackageDTO>[] }> =>
    fetcher.get<{ packages: NonNullable<ActivePackageDTO>[] }>(
      `${baseUrl}/${id}/active-packages`,
    ),

  updatePackageClassSessions: (
    id: string,
    input: UpdatePackageClassSessionsDTO,
  ): Promise<{ package: ActivePackageDTO }> =>
    fetcher.put<{ package: ActivePackageDTO }>(
      `${baseUrl}/${id}/active-package/class-sessions`,
      input,
    ),

  updatePackageSelectedClasses: (
    studentId: string,
    packageId: string,
    input: UpdatePackageSelectedClassesDTO,
  ): Promise<{ package: ActivePackageDTO }> =>
    fetcher.put<{ package: ActivePackageDTO }>(
      `${baseUrl}/${studentId}/packages/${packageId}/selected-classes`,
      input,
    ),

  getPackages: (
    id: string,
    query: OffsetPageQueryDTO,
  ): Promise<OffsetPageDTO<StudentPackageDTO>> => {
    const url = new URL(`${baseUrl}/${id}/packages`);
    url.searchParams.set("limit", String(query.limit ?? 20));
    url.searchParams.set("offset", String(query.offset ?? 0));
    if (query.sort) url.searchParams.set("sort", JSON.stringify(query.sort));
    if (query.filters)
      url.searchParams.set("filters", JSON.stringify(query.filters));
    return fetcher.get<OffsetPageDTO<StudentPackageDTO>>(url.toString());
  },

  getAttendanceSummary: (id: string): Promise<AttendanceSummaryDTO> =>
    fetcher.get<AttendanceSummaryDTO>(`${baseUrl}/${id}/attendance/summary`),

  getAttendance: (
    id: string,
    query: OffsetPageQueryDTO,
  ): Promise<OffsetPageDTO<AttendanceHistoryDTO>> => {
    const url = new URL(`${baseUrl}/${id}/attendance`);
    url.searchParams.set("limit", String(query.limit ?? 20));
    url.searchParams.set("offset", String(query.offset ?? 0));
    if (query.sort) url.searchParams.set("sort", JSON.stringify(query.sort));
    if (query.filters)
      url.searchParams.set("filters", JSON.stringify(query.filters));
    return fetcher.get<OffsetPageDTO<AttendanceHistoryDTO>>(url.toString());
  },

  getActivePrivateLessonPackage: (
    id: string,
  ): Promise<{ package: ActivePrivateLessonPackageDTO | null }> =>
    fetcher.get<{ package: ActivePrivateLessonPackageDTO | null }>(
      `${baseUrl}/${id}/active-private-lesson-package`,
    ),

  listActivePrivateLessonPackages: (
    id: string,
  ): Promise<{ packages: NonNullable<ActivePrivateLessonPackageDTO>[] }> =>
    fetcher.get<{ packages: NonNullable<ActivePrivateLessonPackageDTO>[] }>(
      `${baseUrl}/${id}/active-private-lesson-packages`,
    ),

  listPrivateLessonPackages: (
    id: string,
    query: OffsetPageQueryDTO,
  ): Promise<OffsetPageDTO<PrivateLessonPackageHistoryDTO>> => {
    const url = new URL(`${baseUrl}/${id}/private-lesson-packages`);
    url.searchParams.set("limit", String(query.limit ?? 20));
    url.searchParams.set("offset", String(query.offset ?? 0));
    if (query.sort) url.searchParams.set("sort", JSON.stringify(query.sort));
    if (query.filters)
      url.searchParams.set("filters", JSON.stringify(query.filters));
    return fetcher.get<OffsetPageDTO<PrivateLessonPackageHistoryDTO>>(
      url.toString(),
    );
  },

  listPrivateLessonBookings: (
    id: string,
    query: OffsetPageQueryDTO,
  ): Promise<OffsetPageDTO<PrivateLessonBookingHistoryDTO>> => {
    const url = new URL(`${baseUrl}/${id}/private-lesson-bookings`);
    url.searchParams.set("limit", String(query.limit ?? 20));
    url.searchParams.set("offset", String(query.offset ?? 0));
    if (query.sort) url.searchParams.set("sort", JSON.stringify(query.sort));
    if (query.filters)
      url.searchParams.set("filters", JSON.stringify(query.filters));
    return fetcher.get<OffsetPageDTO<PrivateLessonBookingHistoryDTO>>(
      url.toString(),
    );
  },
};
